import { BaseElement } from '../../../base-element';
import type { ValidationError } from '../../../../state/form.state';
import { registrationActions } from '../registration-state';
import '../../../ui/dt-section-card';
import '../../../ui/dt-input';
import { getSectionCardState } from './section-card-state';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface PremiumSectionChangeDetail {
  section: 'premium';
  field: 'acknowledged' | 'signature';
  path: string;
  value: string | boolean;
}

export class PremiumSection extends BaseElement {
  private _premiumPdfUrl: string | null = null;
  private _acknowledged = false;
  private _signature = '';
  private _errors: ValidationError[] = [];

  get premiumPdfUrl(): string | null {
    return this._premiumPdfUrl;
  }

  set premiumPdfUrl(value: string | null) {
    this._premiumPdfUrl = value;
    this.updateRender();
  }

  get acknowledged(): boolean {
    return this._acknowledged;
  }

  set acknowledged(value: boolean) {
    this._acknowledged = value;
    this.updateRender();
  }

  get signature(): string {
    return this._signature;
  }

  set signature(value: string) {
    this._signature = value;
    this.updateRender();
  }

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getError(path: string): string {
    return this._errors.find((e) => e.field === path)?.message ?? '';
  }

  private emitChange(field: 'acknowledged' | 'signature', value: string | boolean): void {
    const path = `premium.${field}`;
    this.emit<PremiumSectionChangeDetail>('section-change', {
      section: 'premium',
      field,
      path,
      value,
    });
  }

  private attachEventListeners(): void {
    const checkbox = this.$('input[data-field="premium-acknowledged"]') as HTMLInputElement | null;
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        registrationActions.markTouched('premium.acknowledged');
        this.emitChange('acknowledged', checkbox.checked);
      });
    }

    const sigInput = this.$('dt-input[data-field="premium-signature"]');
    if (sigInput) {
      sigInput.addEventListener('input', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        registrationActions.markTouched('premium.signature');
        this.emitChange('signature', detail?.value ?? '');
      });
    }
  }

  private updateRender(): void {
    if (!this._premiumPdfUrl) {
      this.render('');
      return;
    }

    const ackError = this.getError('premium.acknowledged');
    const sigError = this.getError('premium.signature');
    const sectionState = getSectionCardState(['premium'], this._errors);
    const errorCountAttr = sectionState.errorCount > 0 ? `error-count="${sectionState.errorCount}"` : '';
    const completeAttr = sectionState.complete ? 'complete="true"' : '';

    this.render(`
      <dt-section-card
        data-testid="premium-section"
        heading="Premium &amp; Acknowledgment"
        icon="workspace_premium"
        ${errorCountAttr}
        ${completeAttr}
      >
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-neutral-700">Premium Document</span>
            <a
              href="${escapeHtml(this._premiumPdfUrl)}"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs font-semibold text-primary-600 hover:text-primary-700"
              data-testid="premium-open-tab"
            >Open in new tab ↗</a>
          </div>
          <iframe
            src="${escapeHtml(this._premiumPdfUrl)}"
            class="w-full rounded border border-neutral-200"
            style="height: 400px"
            title="Premium PDF"
            data-testid="premium-iframe"
          ></iframe>
          <label class="flex items-start gap-2 text-sm" data-control-path="premium.acknowledged">
            <input
              type="checkbox"
              data-field="premium-acknowledged"
              ${this._acknowledged ? 'checked' : ''}
              class="mt-0.5 h-4 w-4 rounded border-neutral-300"
            />
            <span>I have read and agree to the terms and conditions outlined in the premium document.</span>
          </label>
          ${ackError ? `<p class="text-xs text-error-600">${escapeHtml(ackError)}</p>` : ''}
          <div data-control-path="premium.signature">
            <dt-input
              label="Type your full name to sign"
              value="${escapeHtml(this._signature)}"
              required
              error="${escapeHtml(sigError)}"
              data-field="premium-signature"
              data-path="premium.signature"
            ></dt-input>
          </div>
        </div>
      </dt-section-card>
    `);

    this.attachEventListeners();
  }
}

customElements.define('dt-premium-section', PremiumSection);
